import {
  passkeyAuthenticationVerify,
  passkeyAuthenticationOptions,
  passkeyRegistrationOptions,
  passkeyRegistrationVerify,
} from "./api";

/* ------------------------------- base64url -------------------------------- */

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replace(/-/gu, "+").replace(/_/gu, "/");
  const padding = base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4));
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/u, "");
}

/* ------------------------------ JSON shapes ------------------------------- */

interface CredentialDescriptorJson {
  readonly id: string;
  readonly type: "public-key";
  readonly transports?: readonly string[];
}

interface RequestOptionsJson {
  readonly challenge: string;
  readonly timeout?: number;
  readonly rpId?: string;
  readonly userVerification?: string;
  readonly allowCredentials?: readonly CredentialDescriptorJson[];
}

interface CreationOptionsJson {
  readonly challenge: string;
  readonly rp: { readonly id?: string; readonly name: string };
  readonly user: { readonly id: string; readonly name: string; readonly displayName: string };
  readonly pubKeyCredParams: readonly { readonly type: "public-key"; readonly alg: number }[];
  readonly timeout?: number;
  readonly excludeCredentials?: readonly CredentialDescriptorJson[];
  readonly authenticatorSelection?: Record<string, unknown>;
  readonly attestation?: string;
}

function toDescriptor(json: CredentialDescriptorJson): PublicKeyCredentialDescriptor {
  return {
    id: base64UrlToBytes(json.id),
    type: "public-key",
    ...(json.transports !== undefined
      ? { transports: json.transports as AuthenticatorTransport[] }
      : {}),
  };
}

export function isWebAuthnAvailable(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator.credentials?.get === "function"
  );
}

/** Runs the full WebAuthn assertion ceremony against the API. */
export async function authenticateWithPasskey(email?: string): Promise<void> {
  const options = await passkeyAuthenticationOptions(email !== undefined ? { email } : {});
  const json = options.options as unknown as RequestOptionsJson;
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: base64UrlToBytes(json.challenge),
    ...(json.timeout !== undefined ? { timeout: json.timeout } : {}),
    ...(json.rpId !== undefined ? { rpId: json.rpId } : {}),
    ...(json.userVerification !== undefined
      ? { userVerification: json.userVerification as UserVerificationRequirement }
      : {}),
    ...(json.allowCredentials !== undefined
      ? { allowCredentials: json.allowCredentials.map(toDescriptor) }
      : {}),
  };
  const credential = await navigator.credentials.get({ publicKey });
  if (!(credential instanceof PublicKeyCredential)) {
    throw new Error("Passkey sign-in was cancelled.");
  }
  const assertion = credential.response as AuthenticatorAssertionResponse;
  await passkeyAuthenticationVerify({
    response: {
      id: credential.id,
      rawId: bufferToBase64Url(credential.rawId),
      type: "public-key",
      response: {
        clientDataJSON: bufferToBase64Url(assertion.clientDataJSON),
        authenticatorData: bufferToBase64Url(assertion.authenticatorData),
        signature: bufferToBase64Url(assertion.signature),
        userHandle: assertion.userHandle === null ? null : bufferToBase64Url(assertion.userHandle),
      },
    },
  });
}

/** Registers a new passkey for the signed-in user. */
export async function registerPasskey(name: string): Promise<void> {
  const options = await passkeyRegistrationOptions();
  const json = options.options as unknown as CreationOptionsJson;
  const publicKey: PublicKeyCredentialCreationOptions = {
    challenge: base64UrlToBytes(json.challenge),
    rp: json.rp.id !== undefined ? { id: json.rp.id, name: json.rp.name } : { name: json.rp.name },
    user: {
      id: base64UrlToBytes(json.user.id),
      name: json.user.name,
      displayName: json.user.displayName,
    },
    pubKeyCredParams: [...json.pubKeyCredParams],
    ...(json.timeout !== undefined ? { timeout: json.timeout } : {}),
    ...(json.excludeCredentials !== undefined
      ? { excludeCredentials: json.excludeCredentials.map(toDescriptor) }
      : {}),
  };
  const credential = await navigator.credentials.create({ publicKey });
  if (!(credential instanceof PublicKeyCredential)) {
    throw new Error("Passkey registration was cancelled.");
  }
  const attestation = credential.response as AuthenticatorAttestationResponse;
  await passkeyRegistrationVerify({
    name,
    response: {
      id: credential.id,
      rawId: bufferToBase64Url(credential.rawId),
      type: "public-key",
      response: {
        clientDataJSON: bufferToBase64Url(attestation.clientDataJSON),
        attestationObject: bufferToBase64Url(attestation.attestationObject),
      },
    },
  });
}
